import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/system';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { COLORS } from '@/theme/colors';
import { T } from '@/theme/tokens';

const DemoBubble = styled(Paper)({
  position: 'fixed',
  zIndex: 1400,
  padding: '16px',
  borderRadius: 14,
  boxShadow: T.popShadow,
  background: 'white',
  minWidth: 'min(680px, calc(100vw - 24px))',
  maxWidth: 'min(960px, calc(100vw - 24px))',
  border: `1px solid ${T.border}`,
});

const StepRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '8px',
  padding: '4px 0',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: '#F2F7FD',
  },
});

const StepButton = styled(IconButton)<{
  completed: boolean;
  disabled: boolean;
}>(({ completed, disabled }) => ({
  width: 32,
  height: 32,
  marginRight: '12px',
  backgroundColor: completed
    ? T.success
    : disabled
    ? '#ccc'
    : COLORS.primary,
  color: 'white',
  fontSize: '12px',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: completed
      ? T.success
      : disabled
      ? '#ccc'
      : T.primaryHover,
  },
  '&:disabled': {
    backgroundColor: '#ccc',
    color: '#666',
  },
}));

const demoSteps = [
  {
    number: 0,
    title: 'Reset All',
    description: 'Clear completion status and reset all generated data',
  },
  {
    number: 1,
    title: 'Generate Infrastructure',
    description:
      'Create cloud accounts, subscriptions, resource groups, resources, and seed costs (last months)',
  },
  {
    number: 2,
    title: 'Execute Cost Calculation & Recalculate Monthly Costs',
    description: 'Run cost calculation stored procedure and recalculate monthly cost metadata',
  },
  {
    number: 3,
    title: 'Create Users, Departments & Budgets',
    description: 'Generate users, systems/departments, budgets, and assign budgets',
  },
  {
    number: 4,
    title: 'Assign Resources to Systems',
    description: 'Link resources to their corresponding systems (and update monthly metadata)',
  },
  {
    number: 5,
    title: 'Assign Resources to Users',
    description: 'Assign resource ownership to system owners (and update monthly metadata)',
  },
  {
    number: 6,
    title: 'Generate Recommendations',
    description:
      'Generate and update smart cost recommendations (cost-only, % savings from monthly forecast, capped)',
  },
  {
    number: 7,
    title: 'Update Budget History',
    description: 'Generate budget history and monthly attestations',
  },
  {
    number: 8,
    title: 'Update attestation',
    description: 'Update attestation status based on resource group percentages',
  },
  {
    number: 9,
    title: 'Recalculate Action Center',
    description: 'Execute prc_action_center_refresh_all to refresh action center items',
  },
  {
    number: 10,
    title: 'Generate Disputes',
    description: 'Create sample disputes and messages for demonstration',
  },
];

/** Steps used with `scenario=test` (matches integration test seeding: 1–5, 9). */
const TEST_SCENARIO_STEPS = new Set([0, 1, 2, 3, 4, 5, 9]);

const LS_TEST_MODE = 'demoGeneratorTestMode';
const LS_COMPLETED_DEMO = 'demoStepsCompleted';
const LS_COMPLETED_TEST = 'demoStepsCompleted_test';

interface DemoBubbleProps {
  isVisible: boolean;
  onClose: () => void;
  anchorElement?: HTMLElement | null;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export default function DemoBubbleComponent({
  isVisible,
  onClose,
  anchorElement,
  onMouseEnter,
  onMouseLeave,
}: DemoBubbleProps) {
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [tmUtcNow, setTmUtcNow] = useState<string | null>(null);
  const [tmDate, setTmDate] = useState<string>('');
  const [tmLoading, setTmLoading] = useState(false);
  const [tmError, setTmError] = useState<string | null>(null);
  const [testDataMode, setTestDataMode] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(LS_TEST_MODE) === '1',
  );

  const completedStorageKey = testDataMode ? LS_COMPLETED_TEST : LS_COMPLETED_DEMO;

  const [completed, setCompleted] = useState<{ [key: number]: boolean }>(() => {
    if (typeof window === 'undefined') return {};
    const test = localStorage.getItem(LS_TEST_MODE) === '1';
    const key = test ? LS_COMPLETED_TEST : LS_COMPLETED_DEMO;
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as { [key: number]: boolean };
    } catch (error) {
      console.error('Error parsing saved completion status:', error);
      localStorage.removeItem(key);
      return {};
    }
  });

  // Save completion status to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(completed).length > 0) {
      localStorage.setItem(completedStorageKey, JSON.stringify(completed));
    }
  }, [completed, completedStorageKey]);

  useEffect(() => {
    if (anchorElement && isVisible) {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [anchorElement, isVisible]);

  const loadTimeMachineUtcNow = async () => {
    setTmLoading(true);
    setTmError(null);
    try {
      const data = (await fetchWrapper.get(urls.DevOps.getTimeMachineUtcNow)) as {
        utc_now: string;
      };
      if (data?.utc_now) {
        setTmUtcNow(data.utc_now);
      }
    } catch (e: any) {
      setTmError(e?.message ?? 'Failed to load timemachine date');
    } finally {
      setTmLoading(false);
    }
  };

  useEffect(() => {
    if (!isVisible) return;
    void loadTimeMachineUtcNow();
  }, [isVisible]);

  const setTimeMachineDate = async () => {
    if (!tmDate) return;

    // Expect yyyy-mm-dd; set to 00:00:00.000Z for predictable UTC behavior.
    const [yStr, mStr, dStr] = tmDate.split('-');
    const y = Number(yStr);
    const m = Number(mStr);
    const d = Number(dStr);
    const overrideUtc = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));

    setTmLoading(true);
    setTmError(null);
    try {
      await fetchWrapper.post(urls.DevOps.setTimeMachineUtcNow, {
        utc_datetime: overrideUtc.toISOString(),
      });
      window.dispatchEvent(new Event('timemachine:changed'));
      await loadTimeMachineUtcNow();
    } catch (e: any) {
      setTmError(e?.message ?? 'Failed to set timemachine date');
    } finally {
      setTmLoading(false);
    }
  };

  const clearTimeMachineDate = async () => {
    setTmLoading(true);
    setTmError(null);
    try {
      await fetchWrapper.post(urls.DevOps.clearTimeMachineUtcNow, {});
      setTmDate('');
      window.dispatchEvent(new Event('timemachine:changed'));
      await loadTimeMachineUtcNow();
    } catch (e: any) {
      setTmError(e?.message ?? 'Failed to reset timemachine date');
    } finally {
      setTmLoading(false);
    }
  };

  const executeStep = async (stepNumber: number) => {
    setLoading((prev) => ({ ...prev, [stepNumber]: true }));
    setError(null);

    const scenarioQs = testDataMode ? '?scenario=test' : '';

    try {
      if (stepNumber === 0) {
        // Reset button: Clear status AND reset all data
        await fetchWrapper.post(`${urls.DevOps.resetGenerator}`, {});
        setCompleted({}); // Clear completion status
        localStorage.removeItem(completedStorageKey);
      } else {
        // Regular step execution
        const endpoint = `${urls.DevOps.generatorStep}/${stepNumber}${scenarioQs}`;
        await fetchWrapper.post(endpoint, {});
        setCompleted((prev) => ({ ...prev, [stepNumber]: true }));
      }
    } catch (err: any) {
      setError(`Step ${stepNumber} failed: ${err.message || 'Unknown error'}`);
      console.error('Demo step error:', err);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading((prev) => ({ ...prev, [stepNumber]: false }));
    }
  };

  const executeAllSteps = async () => {
    const stepsToExecute = demoSteps.filter((step) => {
      if (step.number === 0) return false;
      if (testDataMode && !TEST_SCENARIO_STEPS.has(step.number)) return false;
      return true;
    });

    for (const step of stepsToExecute) {
      await executeStep(step.number);
      // Keep a short default delay between demo steps.
      const delayMs = 1000;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  };

  if (!isVisible) return null;

  const bubbleContent = (
    <DemoBubble
      sx={{
        top: position.top,
        left: position.left,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'all 0.2s ease-in-out',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Demo Generator
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click numbered buttons to generate demo data
          {Object.keys(completed).length > 0 && (
            <span style={{ color: T.success, marginLeft: '8px' }}>
              • Status persisted
            </span>
          )}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: '12px' }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Left column: generator controls */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <Chip
              label={testDataMode ? 'Execute All (test: 1–5, 9)' : 'Execute All (1-10)'}
              onClick={executeAllSteps}
              disabled={Object.values(loading).some(Boolean)}
              color="primary"
              size="small"
              sx={{ fontSize: '11px' }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            {demoSteps.map((step) => {
              const stepDisabledInTestMode =
                testDataMode && !TEST_SCENARIO_STEPS.has(step.number);
              const stepBusy = loading[step.number];
              return (
              <StepRow
                key={step.number}
                sx={{
                  opacity: stepDisabledInTestMode ? 0.45 : 1,
                  pointerEvents: stepDisabledInTestMode ? 'none' : undefined,
                }}>
                <StepButton
                  completed={step.number !== 0 && completed[step.number]} // Reset button never shows as completed
                  disabled={stepBusy || stepDisabledInTestMode}
                  onClick={() => executeStep(step.number)}>
                  {loading[step.number] ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : step.number === 0 ? (
                    'R'
                  ) : (
                    step.number
                  )}
                </StepButton>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {step.title}
                    {step.number !== 0 && completed[step.number] && (
                      <span style={{ color: T.success, marginLeft: '8px' }}>
                        ✓
                      </span>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </StepRow>
            );
            })}
          </Box>
        </Box>

        {/* Right column: timemachine controls */}
        <Box
          sx={{
            width: 280,
            maxWidth: 'min(280px, 36vw)',
            flexShrink: 0,
            borderLeft: `1px solid ${T.divider}`,
            pl: 2,
          }}>
          <FormControlLabel
            sx={{
              alignItems: 'flex-start',
              ml: 0,
              mr: 0,
              mb: 2,
              display: 'flex',
            }}
            control={
              <Checkbox
                size="small"
                checked={testDataMode}
                sx={{ pt: 0.25 }}
                onChange={(_, checked) => {
                  setTestDataMode(checked);
                  localStorage.setItem(LS_TEST_MODE, checked ? '1' : '0');
                  const key = checked ? LS_COMPLETED_TEST : LS_COMPLETED_DEMO;
                  const saved = localStorage.getItem(key);
                  if (saved) {
                    try {
                      setCompleted(JSON.parse(saved));
                    } catch {
                      setCompleted({});
                      localStorage.removeItem(key);
                    }
                  } else {
                    setCompleted({});
                  }
                }}
              />
            }
            label={
              <Box sx={{ pr: 0.5 }}>
                <Typography variant="body2" component="span" display="block">
                  Test data
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35 }}>
                  scenario=test (TST). Steps 6–8 and 10 off.
                </Typography>
              </Box>
            }
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Timemachine
          </Typography>
          {tmError ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
              {tmError}
            </Typography>
          ) : tmUtcNow ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              UTC now:
              <br />
              <b>{tmUtcNow}</b>
            </Typography>
          ) : null}

          <TextField
            label="Date (UTC)"
            type="date"
            size="small"
            value={tmDate}
            onChange={(e) => setTmDate(e.target.value)}
            disabled={tmLoading}
            sx={{ width: '100%', mb: 1 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Timemachine date' }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={setTimeMachineDate}
              disabled={!tmDate || tmLoading}
              fullWidth>
              Set
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={clearTimeMachineDate}
              disabled={tmLoading}
              fullWidth>
              Reset
            </Button>
          </Box>
        </Box>
      </Box>
    </DemoBubble>
  );

  return createPortal(bubbleContent, document.body);
}
